import { completeSignup } from '@/lib/auth-service'
import { getUserByEmail, updateUserInfo } from '@/lib/user-service'
import {
  dataStorageLocationOptions,
  employeesOptions,
  industriesOptions
} from '@/utils/constants'
import { useRouter } from 'next/navigation'

import { convertToFormData } from '@/utils/formDataConverter'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { z } from 'zod'
import { DEFAULT_SUBACCOUNT_ID, DEFAULT_LANDING_ID } from '@/utils/constants'

export const passwordSchema = z
  .string()
  .min(8, 'Password length should be at least 8 characters')
  .max(24, 'Password cannot exceed more than 24 characters')
  .refine(value => /[a-z]/.test(value), {
    message: 'Password must contain at least one lowercase letter'
  })
  .refine(value => /[A-Z]/.test(value), {
    message: 'Password must contain at least one uppercase letter'
  })
  .refine(value => /[0-9]/.test(value), {
    message: 'Password must contain at least one digit'
  })
  .refine(value => /[^a-zA-Z0-9]/.test(value), {
    message: 'Password must contain at least one special character'
  })
  .refine(value => /[\W_]/.test(value), {
    message: 'Password must contain at least one special character'
  })

export const dataStorageLocationValues = dataStorageLocationOptions.map(
  option => option.value
) as [string, ...string[]]

export const signupFormSchemaFirstStep = z
  .object({
    email: z.string().email({ message: 'Enter a valid email' }),
    password: passwordSchema,
    confirmPassword: z.string(),

    dataStorageLocation: z
      .enum(dataStorageLocationValues, {
        errorMap: () => ({ message: 'Please select a data storage location' })
      })
      .optional() // Make optional for invite users
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword']
  })

export type SignupFormFirstStepValues = z.infer<
  typeof signupFormSchemaFirstStep
>

export const industryValues = industriesOptions.map(
  industry => industry.value
) as [string, ...string[]]

export const employeeValues = employeesOptions.map(option => option.value) as [
  string,
  ...string[]
]

// export const dataStorageLocationValues = dataStorageLocationOptions.map(
//   option => option.value
// ) as [string, ...string[]]

export const signupFormSchemaThirdStep = z.object({
  companyName: z
    .string()
    .min(1, { message: 'Company name is required' })
    .max(100, {
      message: 'Company name cannot exceed more than 100 characters'
    }),
  industry: z.enum(industryValues, {
    errorMap: () => ({ message: 'Please select a valid industry' })
  }),
  employees: z.enum(employeeValues, {
    errorMap: () => ({ message: 'Please select a valid number of employees' })
  }),
  aiUsageIntent: z
    .array(z.string())
    .min(1, { message: 'Please select at least one option' }),
  businessRole: z.string().min(1, { message: 'Please select your role' })
})

export type SignupFormThirdStepValues = z.infer<
  typeof signupFormSchemaThirdStep
>

export const signupFormSchemaSecondStep = z.object({
  firstName: z
    .string()
    .min(1, { message: 'First name is required' })
    .max(50, { message: 'First name cannot exceed 50 characters' }),
  lastName: z
    .string()
    .min(1, { message: 'Last name is required' })
    .max(50, { message: 'Last name cannot exceed 50 characters' })
})

export type SignupFormSecondStepValues = z.infer<
  typeof signupFormSchemaSecondStep
>

export const useSignupFormFirstStep = () => {
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    watch,
    setValue,
    clearErrors,
    control
  } = useForm({
    resolver: zodResolver(signupFormSchemaFirstStep),
    defaultValues: {
      dataStorageLocation: 'us-west-1',
      email: '',
      password: '',
      confirmPassword: ''
    } as FieldValues
  })

  const onSubmit = (cb: (data: FieldValues) => void) => {
    return handleSubmit((inputData: FieldValues) => {
      cb(inputData)
    })
  }

  const checkEmailExistence = async (
    email: string
  ): Promise<{ success: boolean } | undefined> => {
    setIsLoading(true)

    try {
      const user = await getUserByEmail(email)
      setIsLoading(false)
      if (user.userId) {
        setError('email', {
          type: 'custom',
          message: 'Email is already associated with an existing account'
        })
        return {
          success: false
        }
      }
    } catch (error: any) {
      if (error?.message?.toLowerCase().includes('user not found')) {
        setIsLoading(false)
        return {
          success: true
        }
      } else {
        setError('email', {
          type: 'custom',
          message: error.message
        })
        return {
          success: false
        }
      }
    }
  }

  //invite user signup
  const completeInviteSignup = async (inviteData: FieldValues) => {
    setIsLoading(true)

    try {
      const signupData = {
        email: inviteData.email,
        password: inviteData.password,
        isInviteUser: true,
      }

      const formData = convertToFormData(signupData)
      const response = await completeSignup(formData)

      setIsLoading(false)

      if (response?.success) {
        try {
          // Handle Supabase session setup for invite users
          if (response.userInfo?.session) {
            const { createClient } = await import('@/utils/supabase/client')
            const supabase = createClient()
            await supabase.auth.setSession({
              access_token: response.userInfo.session.access_token,
              refresh_token: response.userInfo.session.refresh_token
            })
            await supabase.auth.updateUser({
              data: {
                has_subscription: true,
                AgencyId: response.agencyId || inviteData.agencyId
              }
            })
            await supabase.auth.refreshSession()
          }

          // Store user data in localStorage
          const userData = {
            userInfo: response.userInfo,
            agencyIds: response.agencyIds || [inviteData.agencyId],
            agencyUser: response.agencyUser || {
              agencyId: inviteData.agencyId,
              role: 'SUBACCOUNT_USER'
            },
            landingId: response.landingId || inviteData.landingId,
            isInviteUser: true
          }
          localStorage.setItem('user', JSON.stringify(userData))
          let subaccountId = DEFAULT_SUBACCOUNT_ID
          let landingId = DEFAULT_LANDING_ID
          const permissions = response.userInfo?.agencyUser?.Permissions
          if (
            permissions &&
            Array.isArray(permissions) &&
            permissions.length > 0
          ) {
            subaccountId = permissions[0]?.subAccountId || DEFAULT_SUBACCOUNT_ID
          } else {
            subaccountId = response?.subaccountId || DEFAULT_SUBACCOUNT_ID
          }
          landingId = response?.landingId || DEFAULT_LANDING_ID
          router.push(
            `/subaccount/${subaccountId}/landings/${landingId}/landings`
          )

          return {
            ...response,
            agencyId: response.agencyId || inviteData.agencyId,
            isInviteUser: true
          }
        } catch (error) {
          console.error('Error setting up invite user session:', error)
          return {
            ...response,
            agencyId: response.agencyId || inviteData.agencyId,
            isInviteUser: true
          }
        }
      } else {
        return {
          success: false,
          message: response?.message || 'Failed to complete invite signup'
        }
      }
    } catch (error: any) {
      setIsLoading(false)
      console.error('Error in completeInviteSignup:', error)

      let errorMessage = 'Invite signup failed'
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error?.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map((err: any) => err.msg || err.message)
            .join(', ')
        }
      } else if (error?.message) {
        errorMessage = error.message
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }

  return {
    register,
    errors,
    isLoading,
    onSubmit,
    checkEmailExistence,
    completeInviteSignup, // Export the new function
    watch,
    setError,
    handleSubmit,
    setValue,
    clearErrors,
    control
  }
}

export const useSignupFormThirdStep = () => {
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    control,
    reset,
    watch
  } = useForm({
    resolver: zodResolver(signupFormSchemaThirdStep),
    defaultValues: {
      companyName: '',
      industry: '',
      employees: '',
      aiUsageIntent: [],
      businessRole: ''
    }
  })

  const onSubmit = async (
    firstStepData: FieldValues,
    inputData: FieldValues
  ) => {
    setIsLoading(true)

    try {
      const signupStep = sessionStorage.getItem('signupStep')
      const urlParams = new URLSearchParams(window.location.search)
      const fromPricing = urlParams.get('from') === 'pricing'
      const isUpdating = fromPricing

      // Check if this is an OAuth user completing company info
      const isOAuthUser = firstStepData?.isOAuthUser || false
      // Check if this is an invite user (skip company setup for invite users)
      const isInviteUser = firstStepData?.isInviteUser || false

      if (isInviteUser) {
        // For invite users, skip the company setup and return success
        setIsLoading(false)
        return {
          success: true,
          agencyId: firstStepData.agencyId,
          message: 'Invite user signup completed successfully'
        }
      }

      if (isUpdating || isOAuthUser) {
        const response = await updateUserInfo({
          companyName: inputData.companyName,
          industry: inputData.industry,
          numOfEmployees: inputData.employees,
          aiUsageIntent: inputData.aiUsageIntent,
          businessRole: inputData.businessRole
        })

        setIsLoading(false)

        if (response?.success) {
          const userData = localStorage.getItem('user')
          if (userData) {
            const user = JSON.parse(userData)
            if (user.userInfo?.user) {
              user.userInfo.user = {
                ...user.userInfo.user,
                companyName: inputData.companyName,
                industry: inputData.industry,
                numOfEmployees: inputData.employees,
                aiUsageIntent: inputData.aiUsageIntent,
                businessRole: inputData.businessRole
              }
              localStorage.setItem('user', JSON.stringify(user))
            }
          }
          return response
        } else {
          setError('companyName', {
            type: 'custom',
            message: response?.error || 'Failed to update user information'
          })
          return response
        }
      } else {
        const combinedData = {
          ...firstStepData,
          ...inputData,
          firstName:
            inputData.firstName || sessionStorage.getItem('firstName') || '',
          lastName:
            inputData.lastName || sessionStorage.getItem('lastName') || ''
        }
        const formData = convertToFormData(combinedData)
        const response = await completeSignup(formData)

        setIsLoading(false)
        if (response?.success && response?.agencyId) {
          try {
            if (response.userInfo) {
              if (response.userInfo.session) {
                const { createClient } = await import('@/utils/supabase/client')
                const supabase = createClient()
                await supabase.auth.setSession({
                  access_token: response.userInfo.session.access_token,
                  refresh_token: response.userInfo.session.refresh_token
                })
                await supabase.auth.updateUser({
                  data: {
                    has_subscription: false,
                    AgencyId: response.agencyId || null
                  }
                })
                await supabase.auth.refreshSession()
              }

              const updatedUserData = {
                userInfo: response.userInfo,
                agencyIds: response.agencyIds || [],
                agencyUser: response.agencyUser,
                landingId: response.landingId
              }
              localStorage.setItem('user', JSON.stringify(updatedUserData))
            } else {
              const userData = localStorage.getItem('user')
              if (userData) {
                const user = JSON.parse(userData)

                if (response.user) {
                  user.userInfo.user = response.user
                  user.agencyUser = response.agencyUser
                  localStorage.setItem('user', JSON.stringify(user))
                }
              }
            }
          } catch (error) {
            console.error('Error updating localStorage:', error)
          }

          return response
        } else if (response?.success && !response?.agencyId) {
          const userData = localStorage.getItem('user')
          let fallbackAgencyId = null

          if (userData) {
            try {
              const user = JSON.parse(userData)
              fallbackAgencyId =
                user.agencyUser?.agencyId ||
                user.userInfo?.user?.agencyId ||
                user.agencyId
            } catch (error) {
              console.error('Error parsing user data from localStorage:', error)
            }
          }

          return {
            ...response,
            agencyId: fallbackAgencyId
          }
        } else {
          const userData = localStorage.getItem('user')
          if (userData) {
            try {
              const user = JSON.parse(userData)
              const existingAgencyId =
                user.agencyUser?.agencyId ||
                user.userInfo?.user?.agencyId ||
                user.agencyId

              if (existingAgencyId) {
                return {
                  success: true,
                  agencyId: existingAgencyId,
                  message: 'Signup completed successfully'
                }
              }
            } catch (error) {
              console.error('Error parsing user data from localStorage:', error)
            }
          }

          return response || { success: false, error: 'Signup failed' }
        }
      }
    } catch (error: any) {
      setIsLoading(false)

      console.error('Error in onSubmit:', error)
      let errorMessage = 'Sign-up failed'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error?.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map((err: any) => err.msg || err.message)
            .join(', ')
        }
      } else if (error?.message) {
        errorMessage = error.message
      }
      const userData = localStorage.getItem('user')
      if (userData) {
        try {
          const user = JSON.parse(userData)
          const existingAgencyId =
            user.agencyUser?.agencyId ||
            user.userInfo?.user?.agencyId ||
            user.agencyId

          if (existingAgencyId) {
            return {
              success: true,
              agencyId: existingAgencyId,
              message: 'Signup completed successfully'
            }
          }
        } catch (parseError) {
          console.error(
            'Error parsing user data from localStorage:',
            parseError
          )
        }
      }

      setError('companyName', {
        type: 'custom',
        message: errorMessage
      })

      return { success: false, error: errorMessage }
    }
  }

  return {
    register,
    control,
    handleSubmit,
    onSubmit,
    isLoading,
    errors,
    reset,
    watch
  }
}

export const useSignupFormSecondStep = () => {
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setError
  } = useForm({
    resolver: zodResolver(signupFormSchemaSecondStep)
  })

  const onSubmit = async (
    previousStepData: FieldValues,
    inputData: FieldValues
  ) => {
    setIsLoading(true)

    const combinedData = { ...previousStepData, ...inputData }
    const formData = convertToFormData(combinedData)
    setIsLoading(false)
    return { success: true }
  }

  return {
    register,
    errors,
    handleSubmit,
    onSubmit,
    isLoading,
    watch,
    setError
  }
}
